// DESIGN.md section 7 — the API contract, verbatim. Frozen; both agents build against this.
// snake_case in Postgres, camelCase on the wire — these types are the wire shape.

export type IdentitySource = 'badge-jwt' | 'kombo' | 'qr-offline' | 'guest' | 'manual'
export type RatingPool = 'rapid' | 'classical'
export type MatchTypeCode = 'rapid11' | 'classic21'
export type Side = 'left' | 'right'
export type MatchStatus = 'live' | 'finished' | 'abandoned'
export type PointSource = 'touch' | 'esp32' | 'cv' | 'gesture' | 'admin' | 'voice'

// Per-match serve-rotation interval (2026-08-31 user feedback item 2) — house default 3, 2 is
// ITTF standard. Mirrors server/app/services/match_service.py's ALLOWED_SERVE_EVERY/DEFAULT_SERVE_EVERY.
export const SERVE_EVERY_OPTIONS: readonly number[] = [2, 3, 5]
export const DEFAULT_SERVE_EVERY = 3

// Per-MODE serves-every default (2026-08-31 round-3 feedback item 2, refines item 2 above):
// toggling the game mode on New match resets serveEvery to that mode's default — rapid/11-point
// is ITTF-standard every 2, classic 21 keeps the house default of every 3. A manual tap on the
// 2/3/5 row after that overrides it until the mode is toggled again (NewMatch.vue's
// pickMatchType). DEFAULT_SERVE_EVERY above stays the server's own omitted-field fallback (3) —
// distinct constant, since the client now always sends an explicit value.
export const MATCH_TYPE_SERVE_EVERY_DEFAULTS: Record<MatchTypeCode, number> = {
  rapid11: 2,
  classic21: 3,
}

export interface RatingSummary {
  rating: number // rounded int
  rd: number
  volatility: number
  matches: number
  wins: number
  losses: number
  provisional: boolean
  ranked: boolean // matches > 0
}

export interface Player {
  id: number
  displayName: string
  firstName: string | null
  lastName: string | null
  email: string | null
  pictureUrl: string | null
  isGuest: boolean
  identitySource: IdentitySource
  createdAt: string
  lastSeenAt: string
  ratings: { rapid: RatingSummary; classical: RatingSummary } // always both keys
}

export interface MatchType {
  code: MatchTypeCode
  name: string
  pointsToWin: number
  serveRotation: number
  winBy: number
  ratingPool: RatingPool
  allowedBestOf: number[]
  defaultBestOf: number
}

export interface Game {
  index: number
  status: 'live' | 'finished'
  scores: { left: number; right: number }
  players: { left: Player; right: Player } // THIS game's physical mapping
  winnerPlayerId: number | null
  firstServer: Side
  startedAt: string
  finishedAt: string | null
}

export interface RatingChange {
  pool: string
  before: number
  after: number
  delta: number
}

export interface Match {
  id: number
  table: string
  matchType: MatchType
  bestOf: number
  serveEvery: number // resolved effective value (never null on the wire)
  status: MatchStatus
  startedAt: string
  finishedAt: string | null
  players: { left: Player; right: Player } // physical mapping AT START
  currentPlayers: { left: Player; right: Player } // physical mapping NOW (after end switches)
  games: Game[]
  currentGameIndex: number | null
  gamesWon: { [playerId: string]: number }
  server: Side | null // physical side serving now
  serverPlayerId: number | null
  banner: 'switch_ends' | null
  winnerPlayerId: number | null
  ratingChanges: { [playerId: string]: RatingChange } | null
  pointsCount: number // non-undone points, for undo affordance
  lastPoint: { side: Side; source: string; at: string } | null
  // Round-13: set when this match belongs to a tournament bracket slot (null otherwise).
  tournamentId: number | null
  tournamentSlotId: number | null
  // Round-31 (2026-08-31) — set at creation from the kiosk's "Fast mode" setting
  // (store/settings.ts). When true, games are entered as a single final score
  // (backend/finalizeGameFast) instead of point-by-point taps: `server`/`serverPlayerId` stay
  // null (never tracked), `games[currentGameIndex]` is always a 0-0 placeholder while live, and
  // `pointsCount` stays 0 for the match's whole life by design — see DESIGN.md §23.
  fastMode: boolean
}

export interface ApiErrorPayload {
  error: string
  code: string
}

export interface TableState {
  table: string
  name: string
  liveMatch: Match | null
}

export interface RankingEntry {
  rank: number
  player: Player
  rating: number
  rd: number
  matches: number
  wins: number
  losses: number
  winRate: number
  provisional: boolean
  lastMatchAt: string | null
}

export interface RankingsResponse {
  pool: RatingPool
  entries: RankingEntry[]
}

export interface VersusPoolStats {
  matches: number
  winsA: number
  winsB: number
  gamesA: number
  gamesB: number
  pointsA: number
  pointsB: number
  lastMatchAt: string | null
}

export interface VersusResponse {
  a: Player
  b: Player
  pools: { [pool: string]: VersusPoolStats }
  recent: Match[] // <= 20, both pools
}

// Cheap, pool-agnostic overall head-to-head — the Live match footer (below the player
// names, pinned at the bottom). Contrast VersusResponse (the Versus screen): per-pool,
// with games/points and recent matches.
export interface HeadToHeadResponse {
  aPlayerId: number
  bPlayerId: number
  matches: number
  winsA: number
  winsB: number
  winRateA: number
  winRateB: number
  // 2026-08-31 round-3 item 3 — global first-serve counts + the resolved suggestion (lower
  // count serves first, ties broken toward the lower player id — see rankings_service.py's
  // get_head_to_head). store/draft.ts consumes suggestedFirstServerPlayerId to preselect
  // NewMatch's "Serves first" toggle; the counts ride along for transparency/future use.
  firstServeCountA: number
  firstServeCountB: number
  suggestedFirstServerPlayerId: number
}

export interface RatingHistoryEntry {
  matchId: number
  at: string
  before: number
  after: number
}

// Round-4 feedback item 2 (Player profile) — this player's most-played opponents overall (both
// pools combined, matches finished only), sorted by matches played together desc. wins/losses
// are from THIS player's perspective. See server/app/services/player_service.py's
// get_top_rivals for the exact aggregation.
export interface RivalEntry {
  player: Player
  matches: number
  wins: number
  losses: number
}

// This player's 1-based rank within each pool's leaderboard (Rating rows with matches > 0,
// ordered by rating desc) — null when unranked in that pool (matches === 0 or no Rating row).
export interface PlayerRank {
  rapid: number | null
  classical: number | null
}

export interface PlayerDetail extends Player {
  recentMatches: Match[] // <= 20
  ratingHistory: { [pool: string]: RatingHistoryEntry[] }
  rank: PlayerRank
  topRivals: RivalEntry[]
}

export interface IdentityResolveResponse {
  player: Player
  source: 'kombo' | 'qr-offline' | 'cache'
}

// DESIGN.md section 7.10 — Tournaments (2026-08-31 round-8 feedback item 21). Elo-seeded single
// elimination, one active tournament at a time (MVP), no editing a bracket once generated.
export type TournamentStatus = 'registration' | 'in_progress' | 'finished'

export type TournamentFormat = 'single_elim' | 'round_robin' | 'rr_knockout'

export interface Tournament {
  id: number
  name: string
  status: TournamentStatus
  format: TournamentFormat
  prize: string | null
  knockoutSize: number | null
  matchTypeCode: MatchTypeCode
  bestOf: number
  seedingMode: string // 'elo_seeded' is the only mode implemented today
  ratingPool: RatingPool
  bracketSize: number | null
  winnerPlayerId: number | null
  createdAt: string
  startedAt: string | null
  finishedAt: string | null
}

export interface TournamentParticipant {
  player: Player
  seed: number | null // null until the bracket is generated
  registeredVia: 'scan' | 'manual' | string // round-14 provenance
}

export interface TournamentBracketSlot {
  stage: 'knockout' | 'group'
  id: number // this is what CreateMatchPayload.tournamentSlotId refers to
  round: number
  slotIndex: number
  playerA: Player | null // null = TBD, this round hasn't resolved that far yet
  playerB: Player | null
  winnerPlayerId: number | null
  matchId: number | null // set once "start" has created the real match for this slot
  isBye: boolean
}

export interface TournamentStandingRow {
  player: Player
  played: number
  wins: number
  losses: number
}

export interface TournamentDetail extends Tournament {
  participants: TournamentParticipant[]
  rounds: TournamentBracketSlot[][] // KNOCKOUT rounds; [] before the bracket is generated
  groupRounds: TournamentBracketSlot[][] // all-v-all phase pairings (round_robin / rr_knockout)
  standings: TournamentStandingRow[] // resolved-group-slot standings; [] when no group phase
}

// Voice fingerprints (round-15, 2026-08-31) — see server/app/routers/voice.py. The kiosk
// forwards kmb-stt utterances (text + optional x-vector "spk" fingerprint + optional WAV) to
// the corpus; profiles are per-player running-mean centroids used to attribute "gano yo"/"punto
// mío" claims to a physical side via cosine similarity (src/voiceScore.ts).
export interface VoiceSamplePayload {
  lang: string
  text: string
  command?: string | null
  playerId?: number | null // set = enrollment ("Train my voice"); null = passive corpus
  spkVector?: number[] | null
  // Round-16 (2026-08-31): which spk model produced spkVector's embedding space
  // (e.g. "vosk-model-spk-0.4") — never compare vectors across a different stamp. Nullable,
  // never backfilled (safe-by-design.md's version-stamp convention).
  spkModelVersion?: string | null
  audioB64?: string | null
  source?: string
}

export interface VoiceSampleOut {
  id: number
  playerId: number | null
  hasAudio: boolean
  hasVector: boolean
}

export interface VoiceProfile {
  playerId: number
  sampleCount: number
  centroid?: number[] | null // present on GET /voice/profiles; used for speaker matching
  // Round-16: the spk model whose embedding space `centroid` lives in — null on
  // pre-round-16 profiles (implicitly "vosk-model-spk-0.4", the only model ever deployed).
  spkModelVersion?: string | null
}

// "Train my voice" progress (round-18, 2026-08-31, item 4) — one row per player+lang, upserted
// by VoiceTraining.vue every time a sentence is completed, so a reload resumes at the right
// sentence instead of restarting from zero. See server/app/models.py::VoiceTrainingProgress.
export interface VoiceTrainingProgress {
  playerId: number
  lang: string
  sentenceIndex: number
  completed: boolean
}

export interface VoiceTrainingProgressPayload {
  playerId: number
  lang: string
  sentenceIndex: number
  completed: boolean
}

// Round-27 (2026-08-31) — corner-claim voice-confirmation observability. See
// server/app/models.py::VoiceConfirmationInteraction and services/confirmFlow.ts.
export type VoiceConfirmationResolution = 'auto' | 'confirmed' | 'denied' | 'timeout' | 'cancelled'
export type VoiceConfirmationAnswerSource = 'voice' | 'tap'

export interface VoiceConfirmationInteractionPayload {
  candidatePlayerId: number
  side: Side
  color: 'red' | 'blue'
  rawCosine: number
  calibratedConfidence: number // 0..1, not a %
  runnerUpPlayerId?: number | null
  runnerUpCosine?: number | null
  margin?: number | null // calibrated-confidence gap, 0..1
  utteranceText: string
  utteranceLang: string
  utteranceDurationSec?: number | null
  resolution: VoiceConfirmationResolution
  answerSource?: VoiceConfirmationAnswerSource | null
  latencyMs?: number | null
  screen?: string | null
  ttsAttempted: boolean
  ttsOk?: boolean | null
  // Round-28 (2026-08-31) — candidate-scope label (services/candidateScope.ts's scopeLabel) and
  // which command this interaction resolves ('corner_claim' | 'point_claim'). Both optional/
  // additive — omitting them still posts a valid request.
  scope?: string | null
  command?: 'corner_claim' | 'point_claim' | null
}

// Round-29 (2026-08-31) — tournament roll-call observability payload. See
// server/app/models.py::RollCallInteraction for field-by-field rationale.
export type RollCallOutcome = 'confirmed' | 'denied' | 'timeout' | 'skipped' | 'cancelled'

export interface RollCallInteractionPayload {
  sessionId: string
  tournamentId: number
  playerId: number
  orderIndex: number
  promptText: string
  promptLang: string
  outcome: RollCallOutcome
  answerSource?: 'voice' | 'tap' | null
  speakerMatchPlayerId?: number | null
  speakerMatchConfidence?: number | null
  speakerMismatch: boolean
  latencyMs?: number | null
  ttsAttempted: boolean
  ttsOk?: boolean | null
}

// Round-30 (2026-09-01) — TTS debug panel (views/DebugTts.vue). See
// server/app/schemas.py's matching Tts* models and app/routers/voice.py's /voice/tts/* debug
// endpoints for field-by-field rationale.
export interface TtsVoice {
  id: string // piper dataset name, e.g. "davefx" — also the cache key's voice-name component
  lang: string
  label: string
  isDefault: boolean
}

export interface TtsTemplate {
  id: string
  label: string
  flow: string
}

export interface TtsCacheEntry {
  id: string
  text: string | null
  lang: string | null
  voice: string | null
  sizeBytes: number
  createdAt: number // epoch seconds
  hits: number
}

export interface TtsEvent {
  flow: string
  text: string
  lang: string
  voice: string | null
  ok: boolean
  cached: boolean
  latencyMs: number | null
  playerId: number | null
  ts: number // epoch seconds
}
